/**
 * Render quality tiers.
 *
 * The spec targeted a three-year-old mid-range Android and the renderer was
 * built down to it — one setting, chosen for the weakest plausible device, and
 * applied to every device. The cost of that landed almost entirely on the
 * phones people actually play flagships on:
 *
 *   `setPixelRatio(min(devicePixelRatio, touch ? 1.5 : 2))`
 *
 * A Galaxy S22 Ultra reports devicePixelRatio 3.5, so that clamp rendered the
 * game at under half of its native resolution and upscaled the result. On a
 * 1440p panel no amount of shading work matters next to that — it is by a wide
 * margin the largest visual difference available on such a device, and it is a
 * one-line change that had been sitting behind a constant chosen for hardware
 * nobody in this conversation owns.
 *
 * Tiers rather than a raised constant, because "make it look good on a
 * flagship" and "still run on a cheap phone" are different answers and the code
 * should be able to hold both. `?quality=low|medium|high|ultra` forces one.
 */

export type QualityTier = 'low' | 'medium' | 'high' | 'ultra'

export interface QualitySettings {
  tier: QualityTier
  /** Cap on devicePixelRatio. The dominant term in both cost and sharpness. */
  pixelRatio: number
  /**
   * MSAA. Cheap on the tile-based GPUs every phone uses — the resolve happens
   * in tile memory and never touches main memory — so switching it *off* for
   * touch was backwards. A snow/sky boundary is the highest-contrast edge that
   * can exist, white against blue, and it crawls without it.
   */
  msaa: boolean
  shadowMapSize: number
  /** Distance, metres, over which procedural detail normals fade out. */
  detailFadeStart: number
  detailFadeEnd: number
  /** True when ?quality= named this tier. Adaptation leaves it alone. */
  forced: boolean
}

// The table describes what a tier *is*. `tier` and `forced` describe how one
// came to be selected, so they are attached at the point of selection.
const TIERS: Record<QualityTier, Omit<QualitySettings, 'tier' | 'forced'>> = {
  low: { pixelRatio: 1.25, msaa: false, shadowMapSize: 1024, detailFadeStart: 40, detailFadeEnd: 130 },
  medium: { pixelRatio: 1.6, msaa: true, shadowMapSize: 2048, detailFadeStart: 55, detailFadeEnd: 170 },
  high: { pixelRatio: 2, msaa: true, shadowMapSize: 2048, detailFadeStart: 70, detailFadeEnd: 220 },
  // Ultra was pixelRatio 3, which on a Galaxy S22 Ultra is 3.3M pixels a frame
  // and measured 50fps on the device. Pixel count is the single largest term in
  // fragment cost, and 2.5 is a 30% cut for a difference nobody can see on a
  // 6-inch panel already rendering well above its own pixel density.
  ultra: { pixelRatio: 2.5, msaa: true, shadowMapSize: 4096, detailFadeStart: 90, detailFadeEnd: 300 },
}

/** Tiers in order, so the adaptive step-down knows which way is cheaper. */
export const TIER_ORDER: readonly QualityTier[] = ['low', 'medium', 'high', 'ultra']

export function tierSettings(tier: QualityTier): QualitySettings {
  return { tier, ...TIERS[tier], forced: false }
}

/**
 * Guess a tier from what the browser will tell us.
 *
 * Deliberately a heuristic and not a device database: those go stale, and the
 * honest fallback is that the HUD reports frame rate and `?quality=` overrides
 * this in one URL. Erring high is the right bias — a flagship held back looks
 * permanently soft, whereas a device that cannot keep up says so immediately
 * and visibly.
 */
export function detectQuality(gl?: WebGLRenderingContext | WebGL2RenderingContext): QualitySettings {
  const forced = new URLSearchParams(location.search).get('quality') as QualityTier | null
  if (forced && forced in TIERS) return { tier: forced, ...TIERS[forced], forced: true }

  const touch = matchMedia('(pointer: coarse)').matches
  if (!touch) return { tier: 'ultra', ...TIERS.ultra, forced: false }

  const cores = navigator.hardwareConcurrency ?? 4
  const dpr = window.devicePixelRatio || 1
  let renderer = ''
  try {
    const dbg = gl?.getExtension('WEBGL_debug_renderer_info')
    if (gl && dbg) renderer = String(gl.getParameter(dbg.UNMASKED_RENDERER_WEBGL))
  } catch { /* the extension is not always exposed */ }

  // The GPU string is the strongest signal when it is available. Adreno 7xx/8xx,
  // Mali-G7xx/G-7xx, and any Apple GPU are current flagship parts.
  const flagshipGpu = /adreno \(tm\) (7|8)\d\d|adreno (7|8)\d\d|mali-g[7-9]\d|apple gpu|apple a\d\d/i.test(renderer)
  if (flagshipGpu && dpr >= 2.5 && cores >= 6) return { tier: 'ultra', ...TIERS.ultra, forced: false }
  if (flagshipGpu || (dpr >= 3 && cores >= 8)) return { tier: 'high', ...TIERS.high, forced: false }
  if (dpr >= 2 && cores >= 4) return { tier: 'medium', ...TIERS.medium, forced: false }
  return { tier: 'low', ...TIERS.low, forced: false }
}
